import React, { useRef, useState, useEffect } from 'react';

import useSize from 'components/Common/Screen/Hooks/useSize';

import Masonry from 'react-responsive-masonry';
import { useNavigate } from 'react-router-dom';
import routes from 'app/routes';
import NextScoreStep from 'components/Members/CoachingReadinessScore/Index';
import AssessmentDebriefScoreStep from 'components/Members/AssessmentDebriefScore/Index';
import { useContext as useUsersContext } from 'components/Users/Context';
import Card from './Card';

export default function Index() {
  const { state: stateUsers } = useUsersContext();

  const wrapper = useRef(null);
  const [countColumns, setCountColumns] = useState(2);
  const [showNextScoreStep, setShowNextScoreStep] = useState(false);
  const [showAssessmentDebriefScoreStep, setShowAssessmentDebriefScoreStep] = useState(false);
  const size = useSize(wrapper);

  const navigate = useNavigate();
  const member = stateUsers.user?.attributes?.specific;

  useEffect(() => {
    if (size) {
      if (size.width < 576) {
        setCountColumns(1);
      } else {
        setCountColumns(2);
      }
    }
  }, [size]);

  useEffect(() => {
    const handleEventRtwCrs = (_event) => {
      setShowNextScoreStep(true);
    };

    const handleEventShowAssessmentDebrief = (_event) => {
      setShowAssessmentDebriefScoreStep(true);
    };

    window.addEventListener('ReturnToWorkCheckCoachingReadinessScore', handleEventRtwCrs);
    window.addEventListener('AiDebriefCheckCoachingReadinessScore', handleEventShowAssessmentDebrief);

    return () => {
      window.removeEventListener('ReturnToWorkCheckCoachingReadinessScore', handleEventRtwCrs);
      window.removeEventListener('AiDebriefCheckCoachingReadinessScore', handleEventShowAssessmentDebrief);
    };
  }, []);

  const titleText = '24/7 Care In Your Pocket';
  const title = member ? titleText : '';

  return (
    <div className="content">
      <section>
        <h1 className="mb-3">
          {title}
        </h1>
        <p className="mb-3">
          Transforming Behavioral Health Through AI Innovation
        </p>
      </section>
      <section ref={wrapper}>
        <Masonry columnsCount={countColumns} gutter="1rem">
          <div key="resources-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Resources"
              description={member?.is_return_to_work
                ? 'Expert tools for work readiness. Career resources at your fingertips.'
                : 'Peak performance tools for optimal results. Reduce stress with expert resources at your fingertips.'}
              onClick={() => navigate('/members/resources')}
              className="resources"
            />
          </div>

          <div key="programs-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Programs"
              description={member?.is_return_to_work ? 'Tailored back-to-work paths. Career reentry made simple.'
                : `Personalized performance pathways designed for excellence. 
                Simple strategies to manage stress and build resilience.`}
              onClick={() => navigate('/members/programs')}
              className="programs"
            />
          </div>

          <div key="chats-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Chats"
              description={member?.is_return_to_work ? `Connect with coaches instantly. 
                Share workplace challenges and wins.`
                : `Connect with health & performance coaches instantly. 
                Share challenges and discover stress management techniques.`}
              onClick={() => navigate(routes.members.chats.index)}
              className="chats"
            />
          </div>

          <div key="schedule-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Schedule"
              description={member?.is_return_to_work ? 'Book workplace coaching sessions. Prepare on your timeline.'
                : 'Book your coaching sessions. Plan your stress reduction timeline.'}
              onClick={() => navigate(routes.members.schedule.index)}
              className="schedule"
            />
          </div>
          <div key="assessments-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Assessments"
              description={member?.is_return_to_work ? 'Discover your workplace strengths. Track readiness progress.'
                : 'Measure your performance potential. Track progress in managing stress factors.'}
              onClick={() => navigate('/members/assessments')}
              className="assessment"
            />
          </div>
          <div key="wallet-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Wallet"
              description={member?.is_return_to_work ? 'Manage coaching investments. Track your readiness resources.'
                : `Invest in your professional growth and wellbeing. 
                Track resources that minimize stress and maximize productivity.`}
              onClick={() => navigate('/members/wallet')}
              className="wallet"
            />
          </div>
          <div key="settings-card" className={`mb-3 ${size !== null && size.width < 750 ? 'vertical' : ''}`}>
            <Card
              title="Settings"
              description={member?.is_return_to_work ? `Personalize your reentry journey. 
                Update workplace preferences.`
                : `Download mobile app for on-the-go performance. 
                Connect to your calendar to reduce scheduling stress.`}
              onClick={() => navigate(routes.members.settings.index)}
              className="settings"
            />
          </div>
        </Masonry>
      </section>
      {showNextScoreStep && <NextScoreStep />}
      {showAssessmentDebriefScoreStep && <AssessmentDebriefScoreStep />}
    </div>
  );
}
